import {
  Box,
  Text,
  Flex,
} from "@chakra-ui/react"
import { Loading, Table, Checkbox, Tooltip, Empty } from "barte-design-system";
import { useMemo } from "react"
export interface ColumnDefinition<T> {
  title: string
  dataIndex: keyof T | "buttonOptions"
  key?: string
  width?: string | number
  align?: 'left' | 'right' | 'center';
  render?: (value: any, record: T, index: number) => React.ReactNode
  onCell?: (record: T, index: number) => React.HTMLAttributes<HTMLElement>
  sorter?: boolean
  sortable?: boolean
  emptyText?: boolean
  sortFunction?: (a: T, b: T) => number
  tooltipMessage?: string
  rowAction?: (record: T, index: number) => React.ReactNode
}

interface RowSelection<T> {
  selectedRowKeys: (string | number)[]
  onChange: (selectedKeys: (string | number)[], selectedRows: T[]) => void
  rowKey?: (record: T) => string | number
}

interface DynamicTableProps<T> {
  data: T[]
  columns: ColumnDefinition<T>[]
  loading?: boolean
  rowKey?: (record: T) => string | number
  onRowClick?: (record: T) => void
  onRow?: (record: T, index: number) => React.HTMLAttributes<HTMLElement>
  locale?: {
      emptyText?: string
  }
  rowSelection?: RowSelection<T>
  customHeight?: string
  pagination?: {
    currentPage: number
    totalPages: number
    pageSize: number
    onPageSizeChange: (size: number) => void
    onPageChange: (page: number) => void
    totalItems: number
  }
}

function DynamicTable<T extends Record<string, any>>({
  data,
  onRowClick,
  columns,
  loading = false,
  rowKey,
  onRow,
  locale = { emptyText: "Sem dados" },
  rowSelection,
  customHeight,
  pagination,
}: DynamicTableProps<T>) {
  const getRowKey = (record: T, index: number) => {
      if (rowSelection?.rowKey) return rowSelection.rowKey(record);
      if (rowKey) return rowKey(record);
      return (record as any).id ?? (record as any).uuid ?? index;
  };

  const barteColumns = useMemo(() => {
      const baseColumns: any[] = columns.map((column) => {
          const baseColumn: any = {
              key: column.key || (column.dataIndex as string),
              title: column.tooltipMessage ? (
                  <Tooltip label={column.tooltipMessage} placement="bottom">
                      <Text fontSize="12px">{column.title}</Text>
                  </Tooltip>
              ) : column.title,
              dataIndex: column.dataIndex as string | string[],
              align: column.align,
              width: column.width,
              sortable: column.sortable || column.sorter || false,
              render: (value: any, record: T, index: number) => {
                  if (column.rowAction) {
                      return (
                          <Box
                              display="inline-flex"
                              alignItems="center"
                              verticalAlign="middle"
                              opacity={0}
                              transition="opacity 0.2s"
                              sx={{ "tr:hover &": { opacity: 1 } }}
                          >
                              {column.rowAction(record, index)}
                          </Box>
                      );
                  }
                  if (column.render) {
                      return column.render(value, record, index);
                  }
                  return value?.toString() || (column.emptyText ? '' : '-');
              },
              onCell: column.onCell,
          };
          return baseColumn;
      });

      if (rowSelection) {
          const allSelected = data?.length > 0 && data.every((record, i) =>
              rowSelection.selectedRowKeys.includes(getRowKey(record, i))
          );

          baseColumns.unshift({
              key: 'selection',
              title: (
                  <Checkbox
                      defaultChecked={allSelected}
                      onChange={() => {
                          if (allSelected) {
                              rowSelection.onChange([], []);
                          } else {
                              const keys = data.map((record, i) => getRowKey(record, i));
                              rowSelection.onChange(keys, data);
                          }
                      }}
                  />
              ),
              width: 40,
              render: (_: any, record: T, index: number) => {
                  const key = getRowKey(record, index);
                  const isSelected = rowSelection.selectedRowKeys.includes(key);
                  return (
                      <Checkbox
                          defaultChecked={isSelected}
                          onChange={() => {
                              const alreadySelected = rowSelection.selectedRowKeys.includes(key);
                              let newKeys: (string | number)[];
                              if (alreadySelected) {
                                  newKeys = rowSelection.selectedRowKeys.filter(k => k !== key);
                              } else {
                                  newKeys = [...rowSelection.selectedRowKeys, key];
                              }
                              const newRows = data.filter((r, i) => newKeys.includes(getRowKey(r, i)));
                              rowSelection.onChange(newKeys, newRows);
                          }}
                      />
                  );
              },
          });
      }

      return baseColumns;
  }, [columns, rowSelection, data]);



  if (loading) {
      return (
          <Box border="1px" borderColor="gray.50" p={4} display="flex" justifyContent="center" alignItems="center" minH={customHeight || "100px"} >
              <Loading type="page" />
          </Box>
      );
  }

  if (!data?.length) {
      return (
          <Flex border="1px" borderColor="gray.50" borderRadius="md" minH="100px" justifyContent="center" alignItems="center" p={8}>
              <Empty type="empty" description={locale.emptyText} showButton={false} />
          </Flex>
      );
  }

  return (
      <Box
          sx={{
              '& thead th': {
                  backgroundColor: '#f9f9f9 !important',
              },
          }}
      >
          <Table<T>
              columns={barteColumns}
              onRowClick={onRowClick}
              data={data}
              rowKey={rowKey || ((record) => {
                  if (rowSelection?.rowKey) return rowSelection.rowKey(record);
                  return (record as any).id ?? (record as any).uuid ?? String(record);
              })}
              pagination={pagination}
          />
      </Box>
  );
}

export { DynamicTable }
export default DynamicTable
